import { useCallback } from "react";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { getCompanyDetails } from "../Backend/profileFunction";
import { subscriptionCheckAction } from "../Backend/authFunction";
import { removeAuthorization } from "../Functions/AuthFunction";
import { useGlobalContext } from "../ContextApi/GlobalContext";
import { isSubscriptionExpired } from "../Constants/subscription";

/**
 * Light session bootstrap: company profile + expiry enforcement only.
 * Heavy dashboard aggregates load via useDashboardData on /dashboard.
 */
export const useSessionBootstrap = () => {
  const navigate = useNavigate();
  const { dispatch } = useGlobalContext();

  const logoutHandler = useCallback(() => {
    removeAuthorization();
    dispatch({ authToken: false, companyData: false, dashboardData: false });
    navigate("/login");
  }, [dispatch, navigate]);

  const handleSubscriptionExpiryCheck = useCallback(
    async (companyData) => {
      if (!isSubscriptionExpired(companyData?.subscription)) return;

      try {
        await subscriptionCheckAction(companyData);
      } catch (e) {
        console.error("Failed to pause forms on expiry:", e);
      }

      await Swal.fire({
        icon: "error",
        title: "Plan Expired",
        text: "Your subscription has expired. All active forms have been paused.",
        confirmButtonText: "Renew Now",
        confirmButtonColor: "#0B1C2D",
        allowOutsideClick: false,
        allowEscapeKey: false,
      }).then((result) => {
        if (result.isConfirmed) {
          navigate("/plans");
        }
      });
    },
    [navigate]
  );

  const bootstrapSession = useCallback(async () => {
    const companyRes = await getCompanyDetails();

    if (companyRes?.success) {
      dispatch({ companyData: companyRes.data });
      await handleSubscriptionExpiryCheck(companyRes.data);
      return { success: true };
    }

    if (companyRes?.error === "Unauthorized: Invalid Token.") {
      await Swal.fire({
        icon: "warning",
        title: "Session Expired",
        text: "Your security token has expired. Please login again to continue.",
        confirmButtonText: "Login",
        confirmButtonColor: "#0B1C2D",
        allowOutsideClick: false,
        allowEscapeKey: false,
      }).then((result) => {
        if (result?.isConfirmed) {
          logoutHandler();
        }
      });
    }

    return { success: false, error: companyRes?.error };
  }, [dispatch, handleSubscriptionExpiryCheck, logoutHandler]);

  return { bootstrapSession, logoutHandler };
};
